import React, { useMemo, useState } from "react";
import { Button, Spinner, Table } from "reactstrap";

import CanceladoSefazBadge, {
  isCanceladoSefaz,
} from "../../../components/cte/CanceladoSefazBadge";
import { fmtBRL, fmtInt, fmtData, labelTipoCte } from "./formatters";
import { rotuloDocumento } from "../../../utils/modeloDocumento";

// PO1-3199: conteúdo do modal "CT-es da Fatura" (Auditoria de Faturas).
// Resumo financeiro + validação, validação rápida (3 atalhos), filtros, seleção
// com ações em massa e tabela enxuta. Faturas com 30+ CT-e (Brazul/Tegma chega a
// 56) eram validadas uma linha por vez.
//
// Regras:
// - atalhos e ações em massa só mudam a marcação local; nada é gravado até
//   "Salvar validação" (mesmo PUT de sempre, que já aceita a lista inteira);
// - atalhos só marcam CT-e ainda pendentes (não desfazem o que já foi marcado);
// - CT-e cancelado na SEFAZ nunca vira OK (PO1-3071; o backend também recusa).
//
// Cores só por variáveis do tema (--everlog-*), para valer no claro e no escuro.

const LIMIAR_DIFERENCA = 0.02; // rateio de centavos não conta como diferença

function diferenca(c) {
  const cob = Number(c.freteCobrado) || 0;
  const sim = Number(c.freteSimulado) || 0;
  return cob - sim;
}

function fmtDiferenca(valor) {
  if (Math.abs(valor) <= LIMIAR_DIFERENCA) return "—";
  const sinal = valor > 0 ? "+" : "−";
  return `${sinal}${fmtBRL(Math.abs(valor))}`;
}

const FILTROS = [
  { id: "todos", label: "Todos" },
  { id: "div", label: "Com divergência" },
  { id: "semdiv", label: "Sem divergência" },
  { id: "pendentes", label: "A validar" },
];

export default function CtesValidacao({
  data,
  editavel,
  saving,
  saveError,
  valorAtual,
  marcar,
  marcarVarios,
  alterados,
  onSalvar,
  onAbrirCte,
  onRecalcular, // PO1-2256: recalcula o frete dos CT-e informados (ids)
  recalculo, // { executando, tom, texto } do recálculo em andamento/último
}) {
  const [filtro, setFiltro] = useState("todos");
  const [busca, setBusca] = useState("");
  const [selecionados, setSelecionados] = useState(() => new Set());

  const pendente = (c) => {
    const v = valorAtual(c);
    return v !== "OOK" && v !== "NOK";
  };
  const divergente = (c) => Boolean(c.divergencia);

  const visiveis = useMemo(() => {
    const termo = busca.replace(/\D/g, "");
    return data.filter((c) => {
      if (termo && !String(c.numero || "").includes(termo)) return false;
      if (filtro === "div") return divergente(c);
      if (filtro === "semdiv") return !divergente(c);
      if (filtro === "pendentes") return pendente(c);
      return true;
    });
    // valorAtual muda a cada marcação; data/filtro/busca definem o recorte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filtro, busca, valorAtual]);

  const totais = useMemo(() => {
    const cobrado = data.reduce((s, c) => s + (Number(c.freteCobrado) || 0), 0);
    const simulado = data.reduce((s, c) => s + (Number(c.freteSimulado) || 0), 0);
    return { cobrado, simulado, diferenca: cobrado - simulado };
  }, [data]);

  const contagem = data.reduce(
    (acc, c) => {
      const v = valorAtual(c);
      if (v === "OOK") acc.ok += 1;
      else if (v === "NOK") acc.nok += 1;
      else acc.pendente += 1;
      if (divergente(c)) acc.div += 1;
      if (pendente(c) && !divergente(c)) acc.pendSemDiv += 1;
      if (pendente(c) && divergente(c)) acc.pendDiv += 1;
      if (pendente(c) && divergente(c) && !isCanceladoSefaz(c)) acc.pendDivAprovaveis += 1;
      return acc;
    },
    { ok: 0, nok: 0, pendente: 0, div: 0, pendSemDiv: 0, pendDiv: 0, pendDivAprovaveis: 0 }
  );
  const contagemFiltro = {
    todos: data.length,
    div: contagem.div,
    semdiv: data.length - contagem.div,
    pendentes: contagem.pendente,
  };

  // Atalhos: só pendentes; OK nunca inclui cancelado na SEFAZ.
  const aplicarAtalho = (ehDivergente, situacao) => {
    const alvo = data.filter(
      (c) =>
        pendente(c) &&
        divergente(c) === ehDivergente &&
        !(situacao === "OOK" && isCanceladoSefaz(c))
    );
    marcarVarios(alvo.map((c) => c.id), situacao);
  };

  const alternarSelecao = (id) =>
    setSelecionados((prev) => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });

  const idsVisiveis = visiveis.map((c) => c.id);
  const selecionadosVisiveis = idsVisiveis.filter((id) => selecionados.has(id)).length;
  const todosVisiveisSelecionados = idsVisiveis.length > 0 && selecionadosVisiveis === idsVisiveis.length;

  const alternarTodosVisiveis = () =>
    setSelecionados((prev) => {
      const novo = new Set(prev);
      if (todosVisiveisSelecionados) idsVisiveis.forEach((id) => novo.delete(id));
      else idsVisiveis.forEach((id) => novo.add(id));
      return novo;
    });

  const aplicarNaSelecao = (situacao) => {
    const ids = data
      .filter((c) => selecionados.has(c.id))
      .filter((c) => !(situacao === "OOK" && isCanceladoSefaz(c)))
      .map((c) => c.id);
    marcarVarios(ids, situacao);
    setSelecionados(new Set());
  };

  const nOkAlterados = alterados.filter((a) => a.situacao === "OOK").length;
  const nNokAlterados = alterados.filter((a) => a.situacao === "NOK").length;
  const percentual = (n) => (data.length ? `${(n / data.length) * 100}%` : "0%");
  const difTone = Math.abs(totais.diferenca) <= LIMIAR_DIFERENCA ? "" : totais.diferenca > 0 ? " is-danger" : " is-success";

  return (
    <div className="conf-ctes">
      <div className="conf-ctes__resumo">
        <div className="conf-ctes__metric">
          <span className="conf-ctes__metric-label">Frete cobrado</span>
          <strong className="conf-ctes__metric-value">{fmtBRL(totais.cobrado)}</strong>
          <span className="conf-ctes__metric-hint">Soma dos CT-e da fatura</span>
        </div>
        <div className="conf-ctes__metric">
          <span className="conf-ctes__metric-label">Frete simulado</span>
          <strong className="conf-ctes__metric-value">{fmtBRL(totais.simulado)}</strong>
          <span className="conf-ctes__metric-hint">Pela tabela de frete</span>
        </div>
        <div className="conf-ctes__metric">
          <span className="conf-ctes__metric-label">Diferença</span>
          <strong className={`conf-ctes__metric-value${difTone}`}>{fmtDiferenca(totais.diferenca)}</strong>
          <span className="conf-ctes__metric-hint">
            {contagem.div ? `${fmtInt(contagem.div)} CT-e com divergência` : "Nenhum CT-e com divergência"}
          </span>
        </div>
        <div className="conf-ctes__metric">
          <span className="conf-ctes__metric-label">Validação</span>
          <div className="conf-ctes__barra" aria-hidden="true">
            <span className="is-ok" style={{ width: percentual(contagem.ok) }} />
            <span className="is-nok" style={{ width: percentual(contagem.nok) }} />
          </div>
          <span className="conf-ctes__legenda">
            <span>OK <b>{fmtInt(contagem.ok)}</b></span>
            <span>NOK <b>{fmtInt(contagem.nok)}</b></span>
            <span>A validar <b>{fmtInt(contagem.pendente)}</b></span>
          </span>
        </div>
      </div>

      <div className="conf-ctes__barra-acoes">
        <div className="conf-ctes__grupo">
          <input
            id="conf-ctes-busca"
            className="conf-ctes__busca"
            type="search"
            placeholder="Buscar nº do CT-e"
            aria-label="Buscar número do CT-e"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <div className="conf-ctes__filtros" role="group" aria-label="Filtrar CT-e">
            {FILTROS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`conf-ctes__filtro${filtro === f.id ? " is-active" : ""}`}
                aria-pressed={filtro === f.id}
                onClick={() => setFiltro(f.id)}
              >
                {f.label} <span className="conf-ctes__filtro-n">{fmtInt(contagemFiltro[f.id])}</span>
              </button>
            ))}
          </div>
        </div>
        {editavel && onRecalcular && (
          <Button
            color="secondary"
            outline
            className="conf-ctes__recalcular"
            onClick={() => onRecalcular(data.map((c) => c.id))}
            disabled={saving || (recalculo && recalculo.executando)}
            title="Recalcula o frete de todos os CT-e da fatura pela tabela atual."
          >
            {recalculo && recalculo.executando ? <Spinner size="sm" /> : `Recalcular todos (${fmtInt(data.length)})`}
          </Button>
        )}
        {editavel && (
          <Button
            color="primary"
            className="conf-ctes__salvar"
            onClick={onSalvar}
            disabled={saving || alterados.length === 0}
          >
            {saving ? (
              <Spinner size="sm" />
            ) : alterados.length ? (
              `Salvar validação (${fmtInt(nOkAlterados)} OK · ${fmtInt(nNokAlterados)} NOK)`
            ) : (
              "Salvar validação"
            )}
          </Button>
        )}
      </div>

      {editavel && (
        <div className="conf-ctes__rapida" role="group" aria-label="Validação rápida">
          <span className="conf-ctes__rapida-label">Validação rápida</span>
          <button
            type="button"
            className="conf-ctes__atalho is-ok"
            disabled={saving || contagem.pendSemDiv === 0}
            onClick={() => aplicarAtalho(false, "OOK")}
            title="Marca como OK todos os CT-e sem divergência ainda pendentes."
          >
            ✓ Sem divergência como OK ({fmtInt(contagem.pendSemDiv)})
          </button>
          <button
            type="button"
            className="conf-ctes__atalho is-nok"
            disabled={saving || contagem.pendDiv === 0}
            onClick={() => aplicarAtalho(true, "NOK")}
            title="Caminho normal: recusa todos os CT-e com divergência ainda pendentes."
          >
            ✕ Com divergência como NOK ({fmtInt(contagem.pendDiv)})
          </button>
          <button
            type="button"
            className="conf-ctes__atalho is-warn"
            disabled={saving || contagem.pendDivAprovaveis === 0}
            onClick={() => aplicarAtalho(true, "OOK")}
            title="Aceita a divergência para o processo seguir: marca como OK os CT-e divergentes ainda pendentes (exceto cancelados na SEFAZ)."
          >
            ⚠ Com divergência como OK ({fmtInt(contagem.pendDivAprovaveis)})
          </button>
        </div>
      )}

      {recalculo && recalculo.texto && (
        <div className={`conf-ctes__recalculo is-${recalculo.tom || "info"}`} role="status">
          {recalculo.executando && <Spinner size="sm" />} {recalculo.texto}
        </div>
      )}

      {editavel && selecionados.size > 0 && (
        <div className="conf-ctes__massa" role="region" aria-label="Ações nos CT-e selecionados">
          <span>
            <b>{fmtInt(selecionados.size)}</b> CT-e selecionado(s)
          </span>
          <button type="button" className="conf-ctes__massa-btn is-ok" onClick={() => aplicarNaSelecao("OOK")} disabled={saving}>
            Marcar OK
          </button>
          <button type="button" className="conf-ctes__massa-btn is-nok" onClick={() => aplicarNaSelecao("NOK")} disabled={saving}>
            Marcar NOK
          </button>
          <button type="button" className="conf-ctes__massa-btn" onClick={() => aplicarNaSelecao(null)} disabled={saving}>
            Limpar marcação
          </button>
          {onRecalcular && (
            <button
              type="button"
              className="conf-ctes__massa-btn"
              onClick={() => {
                onRecalcular(data.filter((c) => selecionados.has(c.id)).map((c) => c.id));
                setSelecionados(new Set());
              }}
              disabled={saving || (recalculo && recalculo.executando)}
            >
              Recalcular frete
            </button>
          )}
          <span className="conf-ctes__massa-espaco" />
          <button type="button" className="conf-ctes__massa-btn" onClick={() => setSelecionados(new Set())}>
            Desmarcar seleção
          </button>
        </div>
      )}

      <div className="table-responsive conf-ctes__tabela">
        <Table className="conf-fatura-table conf-ctes__table" hover>
          <thead className="text-primary">
            <tr>
              {editavel && (
                <th className="conf-ctes__col-sel">
                  <input
                    id="conf-ctes-sel-todos"
                    type="checkbox"
                    aria-label="Selecionar todos os CT-e visíveis"
                    checked={todosVisiveisSelecionados}
                    ref={(el) => {
                      if (el) el.indeterminate = selecionadosVisiveis > 0 && !todosVisiveisSelecionados;
                    }}
                    onChange={alternarTodosVisiveis}
                  />
                </th>
              )}
              <th>Documento</th>
              <th>Emissão</th>
              <th className="num">NF-e</th>
              <th className="num">Cobrado</th>
              <th className="num">Simulado</th>
              <th className="num">Diferença</th>
              <th>Situação</th>
              {editavel && <th>Validação</th>}
            </tr>
          </thead>
          <tbody>
            {visiveis.length === 0 ? (
              <tr>
                <td colSpan={editavel ? 9 : 7} className="conf-ctes__vazio">
                  Nenhum CT-e neste filtro.
                </td>
              </tr>
            ) : (
              visiveis.map((c) => {
                const v = valorAtual(c);
                const d = diferenca(c);
                const cancelado = isCanceladoSefaz(c);
                const complementar = String(c.tipoCte || "").toUpperCase().trim() === "COMPLEMENTAR";
                return (
                  <tr key={c.id} className={divergente(c) ? "conf-ctes__linha--div" : undefined}>
                    {editavel && (
                      <td className="conf-ctes__col-sel">
                        <input
                          type="checkbox"
                          aria-label={`Selecionar ${rotuloDocumento(c.modelo)} ${c.numero}`}
                          checked={selecionados.has(c.id)}
                          onChange={() => alternarSelecao(c.id)}
                        />
                      </td>
                    )}
                    <td>
                      <button
                        type="button"
                        className="conf-ctes__numero"
                        onClick={() => onAbrirCte(c)}
                        title={`Ver detalhes do ${rotuloDocumento(c.modelo)}`}
                      >
                        {c.numero}
                        <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true">
                          <path d="M4 2h6v6M10 2 3 9" fill="none" stroke="currentColor" strokeWidth="1.5" />
                        </svg>
                      </button>
                      <span className="conf-ctes__meta">
                        Série {c.serie} ·{" "}
                        {complementar ? (
                          <span className="conf-fatura-tipo-complementar">{labelTipoCte(c.tipoCte, c.modelo)}</span>
                        ) : (
                          labelTipoCte(c.tipoCte, c.modelo)
                        )}
                        {c.modal ? ` · ${c.modal}` : ""}
                      </span>
                    </td>
                    <td>{fmtData(c.dataEmissao)}</td>
                    <td className="num">{fmtInt(c.qtdNf)}</td>
                    <td className="num">{fmtBRL(c.freteCobrado)}</td>
                    <td className="num">{fmtBRL(c.freteSimulado)}</td>
                    <td
                      className={`num conf-ctes__dif${
                        Math.abs(d) <= LIMIAR_DIFERENCA ? " is-zero" : d > 0 ? " is-danger" : " is-success"
                      }`}
                    >
                      {fmtDiferenca(d)}
                    </td>
                    <td>
                      <div className="conf-ctes__situacao">
                        {divergente(c) ? (
                          <button
                            type="button"
                            className="conf-ctes__badge is-danger conf-ctes__badge--acao"
                            onClick={() => onAbrirCte(c)}
                            title="Ver detalhes da divergência"
                          >
                            Divergente
                          </button>
                        ) : null}
                        <span
                          className={`conf-ctes__badge ${
                            v === "OOK" ? "is-success" : v === "NOK" ? "is-danger" : "is-warning"
                          }`}
                        >
                          {v === "OOK" ? "OK" : v === "NOK" ? "NOK" : c.situacaoNome || "A validar"}
                        </span>
                        {cancelado ? <CanceladoSefazBadge compact /> : null}
                      </div>
                    </td>
                    {editavel && (
                      <td>
                        <div className="conf-fatura-validation-toggle">
                          <button
                            type="button"
                            className={
                              v === "OOK"
                                ? "conf-fatura-validation-toggle__btn is-ok is-active"
                                : "conf-fatura-validation-toggle__btn is-ok"
                            }
                            onClick={() => marcar(c.id, "OOK")}
                            // PO1-3071: cancelado na SEFAZ não pode ser aprovado como OK.
                            disabled={saving || cancelado}
                            title={
                              cancelado
                                ? "CT-e cancelado na SEFAZ: não pode ser validado como OK. Marque NOK."
                                : undefined
                            }
                          >
                            OK
                          </button>
                          <button
                            type="button"
                            className={
                              v === "NOK"
                                ? "conf-fatura-validation-toggle__btn is-nok is-active"
                                : "conf-fatura-validation-toggle__btn is-nok"
                            }
                            onClick={() => marcar(c.id, "NOK")}
                            disabled={saving}
                          >
                            NOK
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
          <tfoot>
            <tr>
              {editavel && <th />}
              <th colSpan={3}>Total da fatura</th>
              <th className="num">{fmtBRL(totais.cobrado)}</th>
              <th className="num">{fmtBRL(totais.simulado)}</th>
              <th className={`num conf-ctes__dif${difTone}`}>{fmtDiferenca(totais.diferenca)}</th>
              <th colSpan={editavel ? 2 : 1} />
            </tr>
          </tfoot>
        </Table>
      </div>

      {editavel && saveError ? (
        <div className="conf-ctes__erro" role="alert">
          {typeof saveError === "object" ? JSON.stringify(saveError) : saveError}
        </div>
      ) : !editavel ? (
        <small className="conf-ctes__nota">
          Validação disponível apenas para faturas Em Análise, Em Tratativa e Contestada.
        </small>
      ) : (
        <small className="conf-ctes__nota">
          Os atalhos e as ações em massa só marcam; nada é gravado até "Salvar validação". CT-e
          cancelado na SEFAZ nunca é marcado como OK.
        </small>
      )}
    </div>
  );
}
